import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Play, BadgePercent, Video, TrendingUp, Mail, Lock, User, AtSign, AlertCircle, Check, Gift, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { MP_CSS, MPlayLogo, ThemeToggle, getMPTheme } from "./VidShared";

const AUTH_CSS = `
.mp-input{display:flex;align-items:center;gap:10px;height:48px;padding:0 16px;border-radius:999px;background:#101a14;border:1px solid var(--mp-line);transition:border-color .15s,box-shadow .15s}
.mp-light .mp-input{background:#fff}
.mp-input:focus-within{border-color:rgba(34,197,94,.65);box-shadow:0 0 0 3px rgba(34,197,94,.18)}
.mp-input input{flex:1;min-width:0;background:transparent;outline:none;border:0;font-size:14px;color:var(--mp-ink)}
.mp-input input::placeholder{color:var(--mp-mut)}
.mp-input>svg{color:var(--mp-mut);flex-shrink:0}
.mp-err{display:flex;align-items:flex-start;gap:8px;border-radius:12px;padding:11px 14px;font-size:13px;font-weight:600;background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.35);color:#fca5a5}
.mp-light .mp-err{color:#b91c1c;background:rgba(239,68,68,.06)}
.mp-err svg{flex-shrink:0;margin-top:1px}
.mp-gtext{color:#4ade80}
.mp-light .mp-gtext{color:#15803d}
.mp-link{color:#4ade80;font-weight:700}
.mp-link:hover{text-decoration:underline}
.mp-light .mp-link{color:#15803d}
.mp-label{font-size:13px;font-weight:700;color:var(--mp-mut)}
.mp-light .mp-hero.text-white,.mp-light .mp-hero .text-white{color:#fff!important}
.mp-light .mp-hero .text-white\\/65{color:rgba(255,255,255,.65)!important}
.mp-light .mp-hero .text-white\\/55{color:rgba(255,255,255,.6)!important}
.mp-light .mp-hero .text-white\\/40{color:rgba(255,255,255,.45)!important}
`;

const PERKS = [
  { icon: Video, t: "One link = one video page", d: "Paste YouTube, Instagram or Facebook URLs. No uploads, no hosting costs." },
  { icon: BadgePercent, t: "Affiliate earnings", d: "Attach products & services to your videos and earn commission per sale." },
  { icon: TrendingUp, t: "Views that pay", d: "Grow followers with Shorts & discovery — eligible views earn real payouts." },
];

function useMpLight() {
  const [light, setLight] = useState(getMPTheme() === "light");
  useEffect(() => {
    const h = () => setLight(getMPTheme() === "light");
    window.addEventListener("mp_theme", h);
    return () => window.removeEventListener("mp_theme", h);
  }, []);
  return light;
}

function Field({ label, icon: Icon, right, testid, ...props }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="mp-label" htmlFor={testid}>{label}</label>
        {right}
      </div>
      <div className="mp-input">
        <Icon className="h-4 w-4" />
        <input id={testid} data-testid={testid} {...props} />
      </div>
    </div>
  );
}

function ErrBox({ err, testid }) {
  if (!err) return null;
  return <div className="mp-err" data-testid={testid}><AlertCircle className="h-4 w-4" />{err}</div>;
}

const SubmitBtn = ({ busy, testid, children }) => (
  <button type="submit" data-testid={testid} disabled={busy}
    className="mp-green w-full h-12 rounded-full font-extrabold text-[15px] flex items-center justify-center gap-2 transition-all active:scale-[.98] disabled:opacity-60">
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
  </button>
);

function Shell({ children, title, sub, testid }) {
  const light = useMpLight();
  return (
    <div className={`mp-root ${light ? "mp-light" : ""}`} data-testid="creator-auth-page" data-theme={light ? "light" : "dark"}>
      <style>{MP_CSS + AUTH_CSS}</style>
      <div className="min-h-screen grid lg:grid-cols-2">
        <div className="mp-hero hidden lg:flex relative flex-col justify-between p-12 overflow-hidden text-white"
          style={{ background: "radial-gradient(800px 480px at 18% -10%, rgba(34,197,94,.22), transparent 60%), linear-gradient(160deg,#04150e 0%,#071009 55%,#0a1f14 100%)" }}>
          <Link to="/videos" className="w-fit" data-testid="creator-auth-logo"><MPlayLogo /></Link>
          <div>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="font-extrabold text-4xl xl:text-5xl tracking-tighter leading-[1.05]">
              Create. Share.<br /><span className="text-emerald-400">Earn on MPlay.</span>
            </motion.h1>
            <p className="text-white/65 mt-4 max-w-md text-[15px]">The creator video platform — paste links, grow followers, monetize every view.</p>
            <div className="space-y-4 mt-10">
              {PERKS.map((p, i) => (
                <motion.div key={p.t} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.1 }} className="flex items-start gap-3.5">
                  <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 border border-white/10" style={{ background: "rgba(34,197,94,.12)" }}><p.icon className="h-5 w-5 text-emerald-400" /></div>
                  <div><p className="font-bold text-sm">{p.t}</p><p className="text-white/55 text-xs mt-0.5">{p.d}</p></div>
                </motion.div>
              ))}
            </div>
          </div>
          <p className="text-white/40 text-xs">Free forever · Affiliate commission only · No video hosting fees</p>
        </div>
        <div className="relative flex items-center justify-center px-5 py-10 sm:px-10">
          <div className="absolute top-4 right-4 sm:top-5 sm:right-6"><ThemeToggle /></div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
            <Link to="/videos" className="lg:hidden inline-block mb-7" data-testid="creator-auth-logo-mobile"><MPlayLogo /></Link>
            <h2 className="font-extrabold text-3xl tracking-tight" style={{ color: "var(--mp-ink)" }} data-testid={testid}>{title}</h2>
            <p className="text-sm mt-1.5" style={{ color: "var(--mp-mut)" }}>{sub}</p>
            <div className="mt-7 mp-panel rounded-3xl p-5 sm:p-7 shadow-2xl">{children}</div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export function CreatorLogin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.user.role !== "CREATOR") { setErr("This is the Creator login — use the main login for your account type"); setBusy(false); return; }
      localStorage.setItem("zylo_token", data.token);
      setUser(data.user);
      toast.success(`Welcome back, ${data.user.name}!`);
      navigate("/creator", { replace: true });
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <Shell title="Creator Login" sub="Access your Creator Studio — videos, links & earnings" testid="creator-login-title">
      <form onSubmit={submit} className="space-y-4" data-testid="creator-login-form">
        <ErrBox err={err} testid="cl-error" />
        <Field label="Email" icon={Mail} testid="cl-email" type="email" required placeholder="you@example.com"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Field label="Password" icon={Lock} testid="cl-password" type="password" required placeholder="••••••••"
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          right={<Link to="/forgot-password" className="mp-link text-xs" data-testid="cl-forgot">Forgot Password?</Link>} />
        <SubmitBtn busy={busy} testid="cl-submit">Login to Creator Studio</SubmitBtn>
        <p className="text-center text-sm" style={{ color: "var(--mp-mut)" }}>New Creator? <Link to="/creator/join" data-testid="cl-join-link" className="mp-link">Join Free →</Link></p>
        <p className="text-center text-xs" style={{ color: "var(--mp-mut)" }}>Not a creator? <Link to="/mkt/auth?mode=login&return=/videos" data-testid="cl-user-login-link" className="mp-link">MILTROO User sign in</Link></p>
      </form>
    </Shell>
  );
}

export function CreatorJoin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", username: "", email: "", phone: "", password: "", confirm: "", ref: (params.get("ref") || "").toUpperCase() });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password !== form.confirm) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/vid/register", { name: form.name, username: form.username, email: form.email, phone: form.phone, password: form.password, ref: form.ref });
      localStorage.setItem("zylo_token", data.token);
      setUser(data.user);
      toast.success("Creator account created — verify your email & mobile to unlock all features");
      navigate("/creator/verify", { replace: true });
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <Shell title="Create Creator Account" sub="Create videos, share content and earn through eligible affiliate products & services" testid="creator-join-title">
      <form onSubmit={submit} className="space-y-4" data-testid="creator-join-form">
        <ErrBox err={err} testid="cj-error" />
        <p className="mp-gtext text-xs font-bold flex items-center gap-1.5"><Check className="h-4 w-4" /> 100% free — earn via affiliate commissions only</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Full Name *" icon={User} testid="cj-name" required placeholder="Your name"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field label="Username" icon={AtSign} testid="cj-username" placeholder="telugu-tech-creator"
            value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
        </div>
        <Field label="Email *" icon={Mail} testid="cj-email" type="email" required placeholder="you@example.com"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Field label="Mobile Number *" icon={Phone} testid="cj-phone" type="tel" required placeholder="+91 98765 43210"
          value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <Field label="Referral Code (optional)" icon={Gift} testid="cj-ref" placeholder="Friend's referral code"
          value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value.toUpperCase() })} />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Password *" icon={Lock} testid="cj-password" type="password" required minLength={6} placeholder="Min 6 characters"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <Field label="Confirm Password *" icon={Lock} testid="cj-confirm" type="password" required placeholder="Repeat password"
            value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
        </div>
        <SubmitBtn busy={busy} testid="cj-submit"><Play className="h-4 w-4 fill-current" /> Create Free Creator Account</SubmitBtn>
        <p className="text-center text-sm" style={{ color: "var(--mp-mut)" }}>Already a creator? <Link to="/creator/login" data-testid="cj-login-link" className="mp-link">Creator Login →</Link></p>
        <p className="text-center text-xs mt-4" style={{ color: "var(--mp-mut)" }}>Just watching videos? <Link to="/mkt/auth?mode=login&return=/videos" data-testid="cj-user-login-link" className="mp-link">MILTROO User Login →</Link></p>
      </form>
    </Shell>
  );
}
